import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  of,
  race,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { AUTH_PROVIDER } from './providers/auth.provider';
import { TokenAuthProvider } from './providers/token-auth-provider.service';
import { AuthTokenStorageService } from './services/auth-token-storage.service';
import { AuthStore } from './store/auth-store.service';
import { AuthStore2 } from './store/auth.store';

export function provideAuthInitializer(
  options = { timeoutMs: 3000 },
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(async () => {
      const authStore = inject(AuthStore);

      if (authStore.isInitialized()) {
        return Promise.resolve();
      }

      const initialized$ = toObservable(authStore.isInitialized).pipe(
        filter(Boolean),
      );

      const timeout$ = timer(options.timeoutMs).pipe(
        tap(() =>
          console.warn(
            `Auth initialization timed out after ${options.timeoutMs}ms`,
          ),
        ),
      );

      await firstValueFrom(race(initialized$, timeout$));
      return undefined;
    }),
  ]);
}

export function provideTokenInitializer(
  options = { timeoutMs: 5000 },
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(async () => {
      const tokenStorage = inject(AuthTokenStorageService);
      const authStore = inject(AuthStore2);

      authStore.getIdentity();

      const loaded$ = toObservable(authStore.isGetIdentityLoaded).pipe(
        filter(Boolean),
      );
      const error$ = toObservable(authStore.getIdentityError).pipe(
        map((error) => !!error),
        catchError((error) => {
          tokenStorage.clear();
          throw error;
        }),
      );

      const timeout$ = timer(options.timeoutMs).pipe(
        tap(() =>
          console.warn(
            `Session restore timed out after ${options.timeoutMs}ms`,
          ),
        ),
      );

      await firstValueFrom(race(loaded$, timeout$, error$));

      return undefined;
    }),
  ]);
}
